import CreateWallPost from "@/components/CreatePost/CreateWallPost";
import PostSkeleton from "@/components/Post/PostSkeleton";
import WallPost from "@/components/Post/WallPost";
import DeleteWallPostDialog from "@/components/Post/components/DeleteWallPostDialog";
import EditWallPost from "@/components/Post/components/EditWallPost";
import { AUTHORITIES } from "@/config/constants";
import { useAppDispatch, useAppSelector } from "@/config/store";
import { hasAnyAuthority } from "@/shared/auth/private-route";
import { IEdoWallPost } from "@/shared/model/edo-wall-post.model";
import { AuthenticationState } from "@/shared/reducers/authentication";
import { EntityState } from "@/shared/reducers/reducer.utils";
import { MessageCircle } from "lucide-react";
import { useEffect, useState } from "react";
import { IPaginationBaseState } from "react-jhipster";
import { Navigate } from "react-router-dom";
import { useSetPageTitle } from "../../context/PageTitleContext";
import { getCurrentUserWallPosts, updateEntity } from "../../shared/reducers/entities/edo-wall-post.reducer";
import Circolari from "./components/Circolari";
import CourseMaterials from "./components/CourseMaterials";
import Eventi from "./components/Eventi";

export const Home = () => {
  const dispatch = useAppDispatch();
  const { entities: edoPostBachecaList, loading, updating, updateSuccess } = useAppSelector<
    EntityState<IEdoWallPost>
  >((state) => state.edoWallPost);
  const { account, isAuthenticated } = useAppSelector<AuthenticationState>(
    (state) => state.authentication
  );

  const todayDate = new Date().toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }).replace(/^./, (c) => c.toUpperCase());
  useSetPageTitle('Il tuo feed', todayDate);

  const [paginationState, setPaginationState] = useState<IPaginationBaseState>({
    activePage: 1,
    itemsPerPage: 10,
    sort: "data",
    order: "DESC",
  });
  const [editPostDialog, setEditPostDialog] = useState<{ open: boolean; entity: IEdoWallPost }>({
    open: false,
    entity: null,
  });
  const [deletePostDialog, setDeletePostDialog] = useState<{ open: boolean; entity: IEdoWallPost }>({
    open: false,
    entity: null,
  });

  const isStudent = hasAnyAuthority(account?.roles ?? [], [AUTHORITIES.ROLE_STUDENT]);

  const getAllEntities = () => {
    dispatch(
      getCurrentUserWallPosts({
        page: paginationState.activePage,
        size: paginationState.itemsPerPage,
        sort: `dataCreation:DESC`,
      })
    );
  };

  const sortEntities = () => {
    getAllEntities();
    const endURL = `?page=${paginationState.activePage}&sort=dataCreation:DESC`;
  };

  useEffect(() => {
    sortEntities();
  }, [paginationState.activePage, paginationState.order, paginationState.sort]);

  const handlePagination = (currentPage: number) => {
    setPaginationState({
      ...paginationState,
      activePage: currentPage,
    });
  };

  const updatePost = async (form: IEdoWallPost) => {
    dispatch(updateEntity(form));
  };

  useEffect(() => {
    if (updateSuccess) {
      getAllEntities();
    }
  }, [updateSuccess]);

  if (isAuthenticated && account) {
    if (hasAnyAuthority(account.roles, [AUTHORITIES.ROLE_PARENT])) {
      return <Navigate to="/parents" />;
    }
  }

  return (
    <>
      <div className="flex-1">
        <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 gap-5 md:grid-cols-12 lg:grid-cols-12">
            <div className="md:col-span-8 lg:col-span-9">
              <div>
                {hasAnyAuthority(account.roles, [
                  AUTHORITIES.ROLE_ADMIN,
                  AUTHORITIES.ROLE_VICE,
                  AUTHORITIES.ROLE_TEACHER,
                ]) ? (
                  <div className="mb-4">
                    <CreateWallPost />
                  </div>
                ) : null}
                <div className="mb-4">
                  {loading && !edoPostBachecaList.length ? (
                    <>
                      <div className="pb-4">
                        <PostSkeleton />
                      </div>
                      <div className="pb-4">
                        <PostSkeleton />
                      </div>
                      <div>
                        <PostSkeleton />
                      </div>
                    </>
                  ) : edoPostBachecaList.length > 0 ? (
                    edoPostBachecaList.map((post) => (
                      <div key={post.id} className="pb-4">
                        <WallPost
                          post={post}
                          onEdit={(post) => setEditPostDialog({ open: true, entity: post })}
                          onDelete={() => setDeletePostDialog({ open: true, entity: post })}
                        />
                      </div>
                    ))
                  ) : (
                    <div className="rounded-3xl border-2 border-dashed border-[var(--color-mockup-border)] bg-[var(--color-surface-secondary)]/30 px-6 py-12 text-center">
                      <div className="mx-auto mb-4 flex h-20 w-20 items-center justify-center rounded-full bg-primary/10">
                        <MessageCircle className="h-10 w-10 text-primary" strokeWidth={1.5} />
                      </div>
                      <h3 className="mb-1 font-heading text-lg font-bold text-[var(--color-mockup-text)]">
                        Nessun post nel feed
                      </h3>
                      <p className="mx-auto max-w-sm text-sm text-[var(--color-mockup-text-mid)]">
                        {hasAnyAuthority(account?.roles ?? [], [
                          AUTHORITIES.ROLE_ADMIN,
                          AUTHORITIES.ROLE_VICE,
                          AUTHORITIES.ROLE_TEACHER,
                        ])
                          ? "Condividi qualcosa con la bacheca usando il box qui sopra: sarà visibile a studenti e colleghi."
                          : "Quando i tuoi docenti pubblicheranno qualcosa, lo troverai qui."}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="md:col-span-4 lg:col-span-3">
              <div className="space-y-4">
                <Circolari />
                <Eventi />
                <CourseMaterials />
              </div>
            </div>
          </div>
        </div>
      </div>
      <EditWallPost
        open={editPostDialog.open}
        post={editPostDialog.entity}
        onClose={() => setEditPostDialog({ open: false, entity: null })}
        updatePost={updatePost}
      />
      <DeleteWallPostDialog
        open={deletePostDialog.open}
        post={deletePostDialog.entity}
        onClose={() => setDeletePostDialog({ open: false, entity: null })}
      />
    </>
  );
};

export default Home;
